/**
 * Lightweight JWT claim reader for the staff console. We don't verify the
 * signature here — that's the API's job; the client only needs the operator's
 * display name + role hint so the topbar can render a greeting and the
 * sidebar can highlight role-relevant entries in a future ticket.
 *
 * The list of claim keys we accept is intentionally generous so a developer
 * pasting any reasonable test JWT (sub / preferred_username / name / email)
 * lands on the dashboard without re-issuing tokens. Mirrors the structure of
 * `apps/web-customer/src/lib/auth/jwt.ts`.
 */
export interface StaffClaims {
  /** Stable subject identifier (service-identity `sub`). */
  readonly subject: string;
  /** Best-effort display name. May be empty. */
  readonly displayName: string;
  /** Best-effort role label (service-identity rollout / resource role). May be empty. */
  readonly role: string;
}

const SUBJECT_KEYS = ['sub', 'userId', 'user_id', 'uid'] as const;
const NAME_KEYS = ['name', 'fullName', 'preferred_username', 'email'] as const;

function base64UrlDecode(segment: string): string {
  let s = segment.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4 !== 0) {
    s += '=';
  }
  return atob(s);
}

function decodeUtf8(binary: string): string {
  try {
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return binary;
  }
}

export function decodeStaffClaims(token: string | null | undefined): StaffClaims | null {
  if (!token) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  const payloadSegment = parts[1];
  if (!payloadSegment) {
    return null;
  }
  let parsed: Record<string, unknown>;
  try {
    const json = decodeUtf8(base64UrlDecode(payloadSegment));
    parsed = JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }

  let subject = '';
  for (const key of SUBJECT_KEYS) {
    const value = parsed[key];
    if (typeof value === 'string' && value.length > 0) {
      subject = value;
      break;
    }
  }
  if (subject.length === 0) {
    return null;
  }

  let displayName = '';
  for (const key of NAME_KEYS) {
    const value = parsed[key];
    if (typeof value === 'string' && value.length > 0) {
      displayName = value;
      break;
    }
  }

  // service-identity emits `realm_access.roles: string[]` and `resource_access.<client>.roles`.
  // We surface the first realm role as a hint for the topbar chip; full RBAC
  // gating lands with libs/web-shared/auth in a follow-up.
  let role = '';
  const realmAccess = parsed['realm_access'];
  if (typeof realmAccess === 'object' && realmAccess !== null) {
    const roles = (realmAccess as Record<string, unknown>)['roles'];
    if (Array.isArray(roles)) {
      const firstNonDefault = roles.find(
        (r) => typeof r === 'string' && r.length > 0 && !r.startsWith('default-'),
      );
      if (typeof firstNonDefault === 'string') {
        role = firstNonDefault;
      }
    }
  }

  return { subject, displayName, role };
}
